/**
 * Unified Shipping Configuration
 * 
 * This file centralizes all shipping options, pricing, and zone definitions
 * for the entire e-commerce platform.
 */

// Minimum order amount required for delivery option
export const MINIMUM_ORDER_FOR_DELIVERY = 1500;

export const SHIPPING_OPTIONS = {
  pickup: {
    id: 'pickup' as const,
    label: 'איסוף עצמי',
    sublabel: 'מגנות הדר',
    description: 'איסוף חינם ממחסני החברה',
  },
  delivery: {
    id: 'delivery' as const,
    label: 'משלוח רגיל',
    sublabel: 'עד שפת המדרכה',
    description: 'משלוח עם פריקה בשפת המדרכה בלבד',
  },
} as const;

export const SHIPPING_PRICES = {
  pickup: { central: 0, remote: 0 },
  delivery: { central: 350, remote: 500 },
} as const;

export const PICKUP_LOCATION = {
  name: 'גנות הדר',
  address: 'מחסני החברה, גנות הדר',
  hours: 'א\'-ה\' 8:00-17:00, ו\' 8:00-13:00',
  phone: '03-1234567',
};

// Central zone cities (between Hadera and Gedera) - Default list
// This can be overridden by database configuration in site_settings
export const CENTRAL_ZONE_CITIES = [
  'תל אביב-יפו',
  'תל אביב',
  'יפו',
  'ראשון לציון',
  'פתח תקווה',
  'נתניה',
  'בני ברק',
  'חולון',
  'רמת גן',
  'בת ים',
  'הרצליה',
  'כפר סבא',
  'רעננה',
  'מודיעין-מכבים-רעות',
  'מודיעין',
  'לוד',
  'רחובות',
  'נס ציונה',
  'יבנה',
  'אשדוד',
  'גדרה',
  'חדרה',
  'זכרון יעקב',
  'אור יהודה',
  'גבעתיים',
  'קריית אונו',
  'רמת השרון',
  'הוד השרון',
  'אזור',
  'יהוד',
  'גני תקווה',
  'קרית עקרון',
  'נחלה',
  'משמר השבעה',
  'גינתון',
  'גני יהודה',
  'ראש העין',
  'שוהם',
  'מזכרת בתיה',
  'קדימה-צורן',
  'קדימה',
  'צורן',
  'בית דגן',
  'גבעת שמואל',
  'קריית מוצקין',
  'נוה ירק',
  'תל מונד',
  'קלנסווה',
  'טירה',
  'כפר יונה',
  'אבן יהודה',
  'כפר ברא',
  'פרדסיה',
  'בית אריה',
  'אלפי מנשה',
  'גבעת עדה',
  'בית ינאי',
  'מכמורת',
  'הרצליה פיתוח',
  'כפר שמריהו',
  'בני עטרות',
  'כפר חבד',
  'אבני חפץ',
  'בן שמן',
  'גן יבנה',
  'גזר',
  'גנות הדר',
  'כפר הס',
  'כרם מהר"ל',
  'מישור אדומים',
  'משמרת',
  'נצר סרני',
  'נחשון',
  'ניר צבי',
  'פלמחים',
  'צפריה',
  'רמות מאיר',
  'רנן',
  'שדות דן',
  'תל יצחק',
  'יד רמב"ם',
  'בארותיים',
  'בית חלקיה',
  'בית עוזיאל',
  'בית חנן',
  'בני ציון',
  'גבעת ברנר',
  'גן שורק',
  'גן שלמה',
  'גני אביב',
  'כפר ביל"ו',
  'כפר מנחם',
  'כפר רות',
  'להבים',
  'מסילת ציון',
  'מקווה ישראל',
  'נוה מבטח',
  'נטעים',
  'רינתיה',
  'שפיר',
  'תימורים',
  'יבנה',
  'רמלה',
];

// All Israeli cities, towns, and settlements (comprehensive list)
const ALL_CITIES_WITH_DUPLICATES = [
  // Central Zone
  ...CENTRAL_ZONE_CITIES,

  // North
  'חיפה',
  'נצרת',
  'עכו',
  'טבריה',
  'נהריה',
  'צפת',
  'קרית שמונה',
  'בית שאן',
  'מגדל העמק',
  'קריית ביאליק',
  'קריית ים',
  'קריית אתא',
  'נשר',
  'טירת כרמל',
  'כרמיאל',
  'עפולה',
  'יקנעם עילית',
  'מעלות-תרשיחא',
  'שלומי',
  'חצור הגלילית',
  'קרית טבעון',
  'גדרה',
  'זכרון יעקב',
  'פרדס חנה-כרכור',
  'בנימינה-גבעת עדה',
  'אור עקיבא',
  'קיסריה',
  'בית שערים',
  'מגדל',
  'ראש פינה',
  'מטולה',
  'קצרין',
  'מזרעה',
  'כפר תבור',
  'אלון הגליל',
  'מעיליא',
  'סאג\'ור',
  'חורפיש',
  'ג\'סר א-זרקא',
  'פקיעין',
  'דבוריה',
  'איילת השחר',
  'יסוד המעלה',
  'מנרה',
  'אביבים',
  'אדמית',
  'אילניה',
  'אליפלט',
  'בית ג\'ן',
  'בנימינה',
  'ג\'ת',
  'דלית אל-כרמל',
  'זרעית',
  'חניתה',
  'טמרה',
  'יובלים',
  'כסרא-סמיע',
  'לפיד',
  'מג\'דל שמס',
  'מגד אל-כרום',
  'מנוף',
  'מעונה',
  'מעיין ברוך',
  'מעלה גמלא',
  'משגב',
  'סמוע\'',
  'עמיעד',
  'עספיא',
  'פקיעין (חדשה)',
  'קלע',
  'ראמה',
  'רמות נפתלי',
  'שזור',
  'שעב',
  'תובל',

  // Jerusalem and surrounding area
  'ירושלים',
  'מבשרת ציון',
  'בית שמש',
  'מעלה אדומים',
  'גבעת זאב',
  'אפרת',
  'בית אל',
  'ביתר עילית',

  // South
  'באר שבע',
  'אילת',
  'אשקלון',
  'קרית גת',
  'עראד',
  'דימונה',
  'שדרות',
  'נתיבות',
  'אופקים',
  'קרית מלאכי',
  'יבנה',
  'גדרה',
  'קרית עקרון',
  'נס ציונה',
  'רחובות',
  'רמלה',
  'לוד',
  'כרמי יוסף',
  'קדומים',
  'אור יהודה',
  'בני עטרות',
  'גני תקווה',
  'אזור',
  'יהוד',
  'נווה מונוסון',
  'עין ורד',
  'קדימה',
  'כוכב יאיר',
  'מתתיהו',
  'גבעתיים',
  'רמת השרון',
  'כפר סבא',
  'רעננה',
  'הרצליה',
  'הוד השרון',
  'ראש העין',
  'יבנאל',
  'אור עקיבא',
  'קיסריה',
  'בנימינה',
  'פרדס חנה',
  'זיכרון יעקב',
  'חדרה',
  'עין עירון',
  'כפר יונה',
  'טירה',
  'באקה אל-גרבייה',
  'חדרה',
  'פרדסיה',
  'בית דגן',
  'רמלה',
  'לוד',
  'גזר',
  'נטעים',
  'שפיר',
  'תימורים',
  'בית עוזיאל',
  'בית חנן',
  'משמר איילון',
  'קרית עקרון',
  'מזכרת בתיה',
  'גני הדר',
  'יבנה',
  'נס ציונה',
  'באר טוביה',
  'גדרה',
  'יד מרדכי',
  'ניר עקיבא',
  'שדה דוד',
  'אילניה',
  'קריית גת',
  'אשקלון',
  'שדרות',
  'נתיבות',
  'אופקים',
  'ערד',
  'דימונה',
  'באר שבע',
  'אילת',
  'מצפה רמון',
  'ירוחם',
  'רהט',
  'חורה',
  'כסיפה',
  'לקיה',
  'תל שבע',
  'ארד',
  'שדה בוקר',
  'מרחב עם',
  'אשלים',
  'גרופית',
  'צופר',
  'נאות סמדר',
  'יהל',
  'לוטן',
  'חולות',
  'רביבים',
  'גבעות בר',
  'שיזפון',
  'עין יהב',
  'פארן',
  'ספיר',
  'שחורת',
  'עין הבשור',
  'מבטחים',
  'אמציה',
  'עזר',
  'נווה מיכאל',
  'כמהין',
  'תושיה',
  'נוגה',
  'תלמים',
  'גבעתי',
  'בארי',
  'נירים',
  'אלומים',
  'רעים',
  'זיקים',
  'כרמיה',
  'נתיב העשרה',
  'דורות',
  'מגן',
  'אור הנר',
  'גברעם',
  'קלחים',
  'צוחר',
  'מבועים',
  'גבים',
  'פטיש',
  'איבים',
  'תושיה',
  'גבולות',
  'רוחמה',
  'שובה',
  'תפרח',
  'ביריה',
  'מלילות',
  'חוות שקמים',
  'כישור',
  'שיבולים',
  'ניר עם',
  'עלומים',
  'סופה',
  'שדה ניצן',
  'שלומית',
  'כפר מימון',
  'כמהין',
  'ביתן אהרון',
  'ניצן',
  'זיקים',
  'ברור חיל',
  'אורים',
  'בני נצרים',
  'בני רעים',
  'בני עטרות',
  'בית הגדי',
  'בית ניר',
  'בית קמה',
  'בני דרום',
  'בני חיל',
  'נווה',
  'צביה',
  'בית שקמה',
  'בית דגן',
  'בית עובד',
  'בית יהושע',
  'בת חפר',
  'בצרה',
  'ביתן',
  'בורגתה',
  'גבעת ברנר',
  'גזר',
  'גינתון',
  'גיתה',
  'גן יבנה',
  'גן שורק',
  'גן שמואל',
  'גן שלמה',
  'גני אביב',
  'גני יוחנן',
  'גני הדר',
  'גנות',
  'גני תקווה',
  'גאליה',
  'גברעם',
  'דבירה',
  'דברת',
  'דוגה',
  'דקל',
  'האון',
  'הוגה',
  'הדר עם',
  'חגור',
  'חלץ',
  'חנתון',
  'חפץ חיים',
  'חצב',
  'חצור אשדוד',
  'יד בנימין',
  'יד חנה',
  'יד מרדכי',
  'יד נתן',
  'יד רמב"ם',
  'ידידיה',
  'יושיביה',
  'יזרעאל',
  'יסוד המעלה',
  'יפעת',
  'יקנעם',
  'ישע',
  'ישרש',
  'כפר אחים',
  'כפר אורה',
  'כפר אז"ר',
  'כפר בילו',
  'כפר בלום',
  'כפר בן נון',
  'כפר ברא',
  'כפר ברוך',
  'כפר דניאל',
  'כפר הס',
  'כפר המכבי',
  'כפר הנגיד',
  'כפר הנשיא',
  'כפר הרא"ה',
  'כפר הרי"ף',
  'כפר ויתקין',
  'כפר ורבורג',
  'כפר חב"ד',
  'כפר חיטים',
  'כפר חיים',
  'כפר חנניה',
  'כפר חסידים א',
  'כפר חסידים ב',
  'כפר חרוב',
  'כפר יאסיף',
  'כפר יהושע',
  'כפר יונה',
  'כפר יחזקאל',
  'כפר ידידיה',
  'כפר כנא',
  'כפר מונש',
  'כפר מימון',
  'כפר מל"ל',
  'כפר מנחם',
  'כפר מנדה',
  'כפר מסריק',
  'כפר מצר',
  'כפר נטר',
  'כפר סבא',
  'כפר סילבר',
  'כפר סירקין',
  'כפר עבודה',
  'כפר עזה',
  'כפר פינס',
  'כפר קאסם',
  'כפר קיש',
  'כפר רוזנואלד (זרעית)',
  'כפר רופין',
  'כפר רות',
  'כפר שמאי',
  'כפר שמואל',
  'כפר שמריהו',
  'כפר תבור',
  'כפר תפוח',
  'כרם בן זמרה',
  'כרם מהר"ל',
  'כרמי יוסף',
  'לביא',
  'להב',
  'להבות הבשן',
  'להבות חביבה',
  'לימן',
  'לכיש',
  'לפידות',
  'לקיה',
  'מבוא חמה',
  'מבואות ים',
  'מבוא מודיעים',
  'מבואות יריחו',
  'מבטחים',
  'מגשימים',
  'מדרך עוז',
  'מנות',
  'מנוף',
  'מנורה',
  'מנחמיה',
  'מסדה',
  'מסילות',
  'מסילת ציון',
  'מסעדה',
  'מעברות',
  'מעגלים',
  'מעגן מיכאל',
  'מעוז חיים',
  'מעיין צבי',
  'מעלה אדומים',
  'מעלה אפרים',
  'מעלה גלבוע',
  'מעלה החמישה',
  'מעלה מכמש',
  'מעלה עירון',
  'מעלה עמוס',
  'מעלה שומרון',
  'מענית',
  'מעש',
  'מפלסים',
  'מצפה נטופה',
  'מצר',
  'מקווה ישראל',
  'מרגליות',
  'משאבי שדה',
  'משגב עם',
  'משואות יצחק',
  'משמר איילון',
  'משמר דוד',
  'משמר הירדן',
  'משמר הנגב',
  'משמר העמק',
  'משמר השבעה',
  'משמר השרון',
  'משמרות',
  'נאות גולן',
  'נהלל',
  'נווה',
  'נווה אור',
  'נווה אטיב',
  'נווה אילן',
  'נווה איתן',
  'נווה דניאל',
  'נווה זוהר',
  'נווה חריף',
  'נווה ים',
  'נווה ימין',
  'נווה ירק',
  'נווה מבטח',
  'נווה מיכאל',
  'נווה שלום',
  'נוגה',
  'נחלה',
  'נחלים',
  'נחם',
  'נחשולים',
  'נחשון',
  'נחשונים',
  'ניצני סיני',
  'ניצנים',
  'ניר אליהו',
  'ניר בנים',
  'ניר גלים',
  'ניר דוד (תל עמל)',
  'ניר משה',
  'ניר עם',
  'ניר עציון',
  'ניר עוז',
  'ניר עקיבא',
  'ניר צבי',
  'ניר יפה',
  'ניר יצחק',
  'ניר יתקין',
  'נירן',
  'נעורים',
  'נעלה',
  'נעמה',
  'נען',
  'נצר חזני',
  'נצר סרני',
  'נשר',
  'סאג\'ור',
  'סאסא',
  'סביון',
  'סגולה',
  'סולם',
  'סופה',
  'סוסיה',
  'עבדון',
  'עברון',
  'עגור',
  'עדי',
  'עדנים',
  'עוזה',
  'עוזייר',
  'עולש',
  'עומר',
  'עופר',
  'עופרה',
  'עוצם',
  'עזוז',
  'עזר',
  'עזריה',
  'עזריאל',
  'עזריקם',
  'עטרת',
  'עידן',
  'עין איילה',
  'עין דור',
  'עין הבשור',
  'עין החורש',
  'עין הנצי"ב',
  'עין העמק',
  'עין השלושה',
  'עין השופט',
  'עין ורד',
  'עין זיוון',
  'עין חוד',
  'עין חצבה',
  'עין חרוד (איחוד)',
  'עין חרוד (מאוחד)',
  'עין יהב',
  'עין יעקב',
  'עין כרם-בי"ס חקלאי',
  'עין כרמל',
  'עין מאהל',
  'עין נקובא',
  'עין עירון',
  'עין צורים',
  'עין קנייא',
  'עין ראפה',
  'עין שמר',
  'עין שריד',
  'עין תמר',
  'עינת',
  'עיר אובות',
  'עלמה',
  'עלמון',
  'עמוקה',
  'עמיעד',
  'עמיר',
  'עמיעוז',
  'עמינדב',
  'עמיקם',
  'עמקה',
  'ענב',
  'עפולה',
  'עצמון שגב',
  'עץ אפרים',
  'עראבה',
  'ערד',
  'ערוגות',
  'ערערה',
  'ערערה-בנגב',
  'עשרת',
  'עתלית',
  'פדואל',
  'פדויים',
  'פדיה',
  'פוריה - כפר עבודה',
  'פוריה - נווה עובד',
  'פוריה עילית',
  'פורת',
  'פטיש',
  'פלך',
  'פלמחים',
  'פני חבר',
  'פסוטה',
  'פעמי תש"ז',
  'פצאל',
  'פקיעין (בוקייעה)',
  'פרדס חנה-כרכור',
  'פרדסיה',
  'פרוד',
  'פרזון',
  'פרי גן',
  'צאלים',
  'צביה',
  'צבעון',
  'צובה',
  'צוחר',
  'צופייה',
  'צופים',
  'צופית',
  'צופר',
  'צוקי ים',
  'צוקים',
  'צור הדסה',
  'צור יצחק',
  'צור משה',
  'צור נתן',
  'צוריאל',
  'צורית',
  'ציפורי',
  'צלפון',
  'צנדלה',
  'צפריה',
  'צפרירים',
  'צפת',
  'צרופה',
  'צרעה',
  'קבוצת יבנה',
  'קדומים',
  'קדימה-צורן',
  'קדמה',
  'קדמת צבי',
  'קדר',
  'קדרון',
  'קדרים',
  'קודייראת א-צאנע (ב)',
  'קוממיות',
  'קורנית',
  'קטורה',
  'קיבוץ אילון',
  'קיבוץ גלויות',
  'קיסריה',
  'קלחים',
  'קליה',
  'קלנסווה',
  'קלע',
  'קצרין',
  'קרית אונו',
  'קרית ארבע',
  'קרית אתא',
  'קרית ביאליק',
  'קרית גת',
  'קרית טבעון',
  'קרית ים',
  'קרית יערים',
  'קרית יערים (מוסד)',
  'קרית מוצקין',
  'קרית מלאכי',
  'קרית נטפים',
  'קרית ענבים',
  'קרית עקרון',
  'קרית שלמה',
  'קרית שמונה',
  'קרני שומרון',
  'ראמה',
  'ראס אל-עין',
  'ראש העין',
  'ראש פינה',
  'ראש צורים',
  'ראשון לציון',
  'רביבים',
  'רביד',
  'רגבה',
  'רגבים',
  'רהט',
  'רווחה',
  'רוויה',
  'רוח מדבר',
  'רוחמה',
  'רומת הייב',
  'רועי',
  'רותם',
  'רחוב',
  'רחובות',
  'ריחאניה',
  'ריחן',
  'ריינה',
  'רימונים',
  'רינתיה',
  'רכסים',
  'רם-און',
  'רמות',
  'רמות השבים',
  'רמות מאיר',
  'רמות מנשה',
  'רמות נפתלי',
  'רמלה',
  'רמת גן',
  'רמת דוד',
  'רמת הכובש',
  'רמת השופט',
  'רמת השרון',
  'רמת יוחנן',
  'רמת ישי',
  'רמת מגשימים',
  'רמת צבי',
  'רמת רזיאל',
  'רמת רחל',
  'רנן',
  'רעים',
  'רעננה',
  'רקפת',
  'רשפון',
  'רתמים',
  'שאר ישוב',
  'שבי דרום',
  'שבי ציון',
  'שבי שומרון',
  'שבלי - אום אל-גנם',
  'שגב-שלום',
  'שדה אילן',
  'שדה אליהו',
  'שדה אליעזר',
  'שדה בוקר',
  'שדה דוד',
  'שדה ורבורג',
  'שדה יואב',
  'שדה יעקב',
  'שדה יצחק',
  'שדה משה',
  'שדה נחום',
  'שדה נחמיה',
  'שדה ניצן',
  'שדה עוזיהו',
  'שדה צבי',
  'שדות ים',
  'שדות מיכה',
  'שדי אברהם',
  'שדי חמד',
  'שדי תרומות',
  'שדמה',
  'שדמות דבורה',
  'שדמות מחולה',
  'שדרות',
  'שהם',
  'שואבה',
  'שובב',
  'שובל',
  'שוהם',
  'שומרה',
  'שומריה',
  'שוקדה',
  'שורש',
  'שורשים',
  'שושנת העמקים',
  'שזור',
  'שחר',
  'שחרות',
  'שיבולים',
  'שייח\' דנון',
  'שיטים',
  'שילה',
  'שילת',
  'שכניה',
  'שלווה',
  'שלווה במדבר',
  'שלוחות',
  'שלומי',
  'שלומית',
  'שמיר',
  'שמעה',
  'שמרת',
  'שמשית',
  'שני',
  'שניר',
  'שעב',
  'שעורים',
  'שעל',
  'שעלבים',
  'שער אפרים',
  'שער הגולן',
  'שער העמקים',
  'שער מנשה',
  'שפיים',
  'שפיר',
  'שפר',
  'שפרעם',
  'שקד',
  'שקף',
  'שרונה',
  'שריגים (לי-און)',
  'שריד',
  'שרשרת',
  'שתולה',
  'שתולים',
  'תאשור',
  'תדהר',
  'תובל',
  'תומר',
  'תושיה',
  'תימורים',
  'תירוש',
  'תל אביב-יפו',
  'תל יוסף',
  'תל יצחק',
  'תל מונד',
  'תל עדשים',
  'תל קציר',
  'תל שבע',
  'תל תאומים',
  'תלם',
  'תלמי אליהו',
  'תלמי אלעזר',
  'תלמי ביל"ו',
  'תלמי יוסף',
  'תלמי יחיאל',
  'תלמי יפה',
  'תלמים',
  'תמרת',
  'תנובות',
  'תעוז',
  'תפרח',
  'תקומה',
  'תקוע',
  'תראבין א-צאנע (ישיבה)',
];

// Export deduplicated cities list
export const ALL_ISRAELI_CITIES = Array.from(new Set(ALL_CITIES_WITH_DUPLICATES));

export type ShippingOption = 'pickup' | 'delivery' | 'crane';
export type ShippingZone = 'central' | 'remote';

/**
 * Get shipping zone for a city
 */
export function getShippingZone(city: string): ShippingZone {
  return CENTRAL_ZONE_CITIES.includes(city) ? 'central' : 'remote';
}

/**
 * Calculate shipping cost based on option and zone
 */
export function calculateShippingCost(
  option: ShippingOption,
  zone: ShippingZone,
  overrides?: { central?: number; remote?: number }
): number {
  if (option === 'delivery' && overrides) {
    return zone === 'central'
      ? (overrides.central ?? SHIPPING_PRICES.delivery.central)
      : (overrides.remote ?? SHIPPING_PRICES.delivery.remote);
  }
  return SHIPPING_PRICES[option][zone];
}

/**
 * Get shipping option label
 */
export function getShippingLabel(option: ShippingOption): string {
  return SHIPPING_OPTIONS[option].label;
}

/**
 * Format shipping price for display
 */
export function formatShippingPrice(price: number): string {
  return price === 0 ? 'חינם' : `₪${price}`;
}

export const SHIPPING_WARNINGS = {
  delivery: {
    title: 'משלוח רגיל',
    description: 'פריקה עצמית בשפת המדרכה בלבד. אנו לא אחראים על העברה לבית.',
  },
  crane: {
    title: 'משלוח עם מנוף',
    description: 'כולל פריקה מכנית באמצעות מנוף. מתאים למשקולות כבדות.',
  },
  pickup: {
    title: 'פרטי איסוף',
    description: 'באפשרותך להגיע ולאסוף את המוצר ממחסני החברה',
    address: PICKUP_LOCATION.address,
    hours: PICKUP_LOCATION.hours,
    phone: PICKUP_LOCATION.phone,
  },
};
